import { post, get } from 'aws-amplify/api';
import * as Sentry from '@sentry/react';
import { ApiInput, RestApiOptionsBase } from '@aws-amplify/api-rest/dist/esm/types';
import {
  sendApplicationError,
  sendException,
  FINGERPRINT_APPLICATION_ERROR,
  FINGERPRINT_SYSTEM_ERROR,
  createCustomError
} from '@/lib/sentryClient';
import { Result } from '@/lib/axiosClient';

// RESTAPIのPOSTをラップする
export const executePost = async <T>(input: ApiInput<RestApiOptionsBase>): Promise<Result<T>> => {
  try {
    const operation = post(input);
    const response = await operation.response;
    const extra = {
      Rest: {
        method: 'POST',
        APIName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams),
        response: ''
      }
    };
    if (response.statusCode >= 400 && response.statusCode < 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_APPLICATION_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendApplicationError(error, input.apiName, extra);
      return {
        result: false,
        error: error
      };
    }
    if (response.statusCode >= 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendException(error, input.apiName, extra);
      return {
        result: false,
        error: error
      };
    }
    const data: T = (await response.body.json()) as T;

    Sentry.addBreadcrumb({
      category: 'REST',
      message: `${input.apiName} executed`,
      data: {
        method: 'POST',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        queryParams: JSON.stringify(input.options?.queryParams),
        header: JSON.stringify(input.options?.headers),
        response: JSON.stringify(data)
      }
    });
    return {
      result: true,
      data: data
    };
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
      error.stack = e.stack;
    }
    error.name = `${input.apiName} ${error.name}`;
    const extra = {
      Rest: {
        method: 'POST',
        apiName: input.apiName,
        path: input.path,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    sendException(error, input.apiName, extra);

    return {
      result: false,
      error: error
    };
  }
};

// RESTAPIのGETをラップする
export const executeGet = async <T>(input: ApiInput<RestApiOptionsBase>): Promise<Result<T>> => {
  try {
    const operation = get(input);
    const response = await operation.response;
    const extra = {
      Rest: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        response: '',
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    if (response.statusCode >= 400 && response.statusCode < 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendApplicationError(error, input.apiName, extra);
      return {
        result: false,
        error: error
      };
    }
    if (response.statusCode >= 500) {
      const error = createCustomError(
        new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}: ${JSON.stringify(response.body)}`)
      );
      error.name = `${input.apiName} ${error.name}`;
      extra.Rest['response'] = JSON.stringify(response);
      sendException(error, input.apiName, extra);
      return {
        result: false,
        error: error
      };
    }

    const data: T = (await response.body.json()) as T;

    Sentry.addBreadcrumb({
      category: 'REST',
      message: `${input.apiName} executed`,
      data: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        statusCode: response.statusCode,
        request: JSON.stringify(input.options?.body),
        queryParams: JSON.stringify(input.options?.queryParams),
        header: JSON.stringify(input.options?.headers),
        response: JSON.stringify(data)
      }
    });

    return {
      result: true,
      data: data
    };
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${input.apiName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
      error.stack = e.stack;
    }
    error.name = `${input.apiName} ${error.name}`;
    const extra = {
      Rest: {
        method: 'GET',
        apiName: input.apiName,
        path: input.path,
        request: JSON.stringify(input.options?.body),
        header: JSON.stringify(input.options?.headers),
        queryParams: JSON.stringify(input.options?.queryParams)
      }
    };
    sendException(error, input.apiName, extra);
    return {
      result: false,
      error: error
    };
  }
};
